import { useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import Magnetic from "@/components/Magnetic";
import { scrollToId } from "@/lib/lenis";

export default function Hero({ introDone }) {
    const vidRef = useRef(null);

    useEffect(() => {
        if (!introDone) return;
        vidRef.current?.play().catch(() => {});

        let timer;
        let cancelled = false;
        const schedule = () => {
            const d = vidRef.current?.duration;
            const wait = Number.isFinite(d) ? Math.max(3500, d * 1000 - 300) : 7000;
            timer = setTimeout(() => {
                if (!cancelled && window.scrollY < 40) scrollToId("statement");
            }, wait);
        };
        const v = vidRef.current;
        if (v?.readyState >= 1) schedule();
        else v?.addEventListener("loadedmetadata", schedule, { once: true });

        const cancel = () => {
            cancelled = true;
            clearTimeout(timer);
        };
        window.addEventListener("wheel", cancel, { once: true });
        window.addEventListener("touchmove", cancel, { once: true });
        return () => {
            cancel();
            v?.removeEventListener("loadedmetadata", schedule);
            window.removeEventListener("wheel", cancel);
            window.removeEventListener("touchmove", cancel);
        };
    }, [introDone]);

    return (
        <section data-testid="hero-section" className="relative h-screen w-full overflow-hidden">
            <motion.video
                ref={vidRef}
                initial={{ clipPath: "inset(14% 7% 14% 7%)", scale: 1.06 }}
                animate={introDone ? { clipPath: "inset(0% 0% 0% 0%)", scale: 1 } : {}}
                transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
                poster="/media/showreel-poster.jpg"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                className="h-full w-full object-contain lg:object-cover"
            >
                <source src="/media/showreel.webm" type="video/webm" />
                <source src="/media/showreel.mp4" type="video/mp4" />
            </motion.video>
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#132B21]/70 via-transparent to-[#132B21]/40" />
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={introDone ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 1, delay: 1, ease: [0.16, 1, 0.3, 1] }}
                className="absolute inset-x-0 bottom-0 flex items-end justify-between px-5 pb-8 sm:px-10"
            >
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#F4F4F0]/80">
                    BVISION Designer Studio — Showreel 2026
                    <span className="mt-2 block text-[#F4F4F0]/60">Wuppertal, DE</span>
                </p>
                <Magnetic>
                    <button
                        data-testid="hero-scroll-down"
                        data-cursor="link"
                        onClick={() => scrollToId("statement")}
                        aria-label="Weiter scrollen"
                        className="flex h-14 w-14 items-center justify-center rounded-full border border-white/30 text-[#F4F4F0] transition-colors duration-300 hover:border-[#E63946] hover:bg-[#E63946]"
                    >
                        <ArrowDown size={18} />
                    </button>
                </Magnetic>
            </motion.div>
        </section>
    );
}
